import type {
  RegisterV2Params,
  LoginV2Params,
  ChangePasswordV2Params,
  SetPasswordV2Params,
  PasswordResetRequestV2Params,
  PasswordResetVerifyV2Params,
  AuthV2Response,
  RefreshV2Response,
  ChangePasswordV2Response,
  SetPasswordV2Response,
  PasswordResetRequestV2Response,
  PasswordResetVerifyV2Response,
} from '../model/types'
import { useApi } from '@shared/api'

/**
 * API-клиент авторизации пользователя.
 * Базовый URL: /api/v2/auth
 */
class APIAuth {
  register(data: RegisterV2Params) {
    return useApi(true).post<AuthV2Response>('/auth/register', { body: data })
  }

  login(data: LoginV2Params) {
    return useApi(true).post<AuthV2Response>('/auth/login', { body: data })
  }

  /**
   * Обновление access-токена.
   * ❌ Authorization-заголовок не нужен (interceptor пропускает /auth/refresh).
   * @param refreshToken — опционально; если не передан, бэкенд читает из cookies.
   */
  refresh(refreshToken?: string) {
    return useApi(true).post<RefreshV2Response>('/auth/refresh', {
      body: refreshToken ? { refreshToken } : {},
    })
  }

  changePassword(data: ChangePasswordV2Params) {
    return useApi(true).post<ChangePasswordV2Response>('/auth/change-password', { body: data })
  }

  setPassword(data: SetPasswordV2Params) {
    return useApi(true).post<SetPasswordV2Response>('/auth/set-password', { body: data })
  }

  requestPasswordReset(data: PasswordResetRequestV2Params) {
    return useApi(true).post<PasswordResetRequestV2Response>('/auth/password-reset/request', { body: data })
  }

  verifyPasswordReset(data: PasswordResetVerifyV2Params) {
    return useApi(true).post<PasswordResetVerifyV2Response>('/auth/password-reset/verify', { body: data })
  }

  checkPhoneVerification(data: { phone: string; institutionId?: string | number }) {
    return useApi(true).post('/auth/check-phone-verification', { body: data })
  }

  logout(data: { id: string | number }) {
    return useApi(true).post<{ message: string }>('/auth/logout', { body: data })
  }

  sendVerificationEmail(data: { email: string; institutionId: string | number }) {
    return useApi(true).post('/auth/send-verification-email', { body: data })
  }

  verifyEmail(data: { token: string }) {
    return useApi(true).post('/auth/verify-email', { body: data })
  }

  generateTelegramToken(data: {
    institutionId: string
    userAgent?: string
    clientFingerprint?: string
  }) {
    return useApi(true).post<{
      authToken: string
      sessionId: string
      expiresAt: string
      expiresInSeconds: number
      telegramLink: string
    }>('/auth/telegram/generate-token', { body: data })
  }

  checkTelegramStatus(sessionId: string) {
    return useApi(true).get<{
      status: 'pending' | 'completed' | 'expired'
      accessToken?: string
      refreshToken?: string
      sessionId: string
      user?: any
      message: string
      remainingSeconds?: number
    }>(`/auth/telegram/status/${sessionId}`)
  }

  initiateYandexAuth(data: {
    institutionId: string
    userAgent?: string
    clientFingerprint?: string
  }) {
    return useApi(true).post<{
      authToken: string
      sessionId: string
      expiresAt: string
      expiresInSeconds: number
      yandexAuthUrl: string
      instructions: string
    }>('/auth/yandex/initiate', { body: data })
  }

  /**
   * Авторизация в мини-приложении MAX (WebApp).
   * Без `contact` бэкенд вернёт `requiresContact: true` — тогда нужно вызвать
   * WebApp.requestContact() и повторить запрос с полученным контактом.
   */
  maxWebAppAuth(data: {
    institutionId: string
    initData: string
    contact?: { phone: string; authDate: string | number; hash: string }
    /** Отпечаток устройства — нужен анти-абузу приветственных баллов. */
    clientFingerprint?: string
  }) {
    return useApi(true).post<{
      requiresContact?: boolean
      accessToken?: string
      refreshToken?: string
      sessionId?: string
      user?: {
        id: string
        phoneNumber?: string
        firstName?: string
        lastName?: string
        institutionId: string
      }
    }>('/auth/max/webapp', { body: data })
  }

  /**
   * Генерация токена для входа через MAX-бота (Сценарий A, on-site вход).
   * POST /auth/max/generate-token → возвращает deep-link в бота + sessionId для поллинга.
   */
  generateMaxToken(data: { institutionId: string; userAgent?: string; clientFingerprint?: string }) {
    return useApi(true).post<{
      authToken: string
      sessionId: string
      expiresAt: string
      expiresInSeconds: number
      maxLink: string
    }>('/auth/max/generate-token', { body: data })
  }

  /**
   * Проверка статуса входа через MAX-бота (поллинг Сценария A).
   * GET /auth/max/status/:sessionId
   */
  checkMaxStatus(sessionId: string) {
    return useApi(true).get<{
      status: 'pending' | 'verified' | 'used' | 'expired' | 'failed'
      accessToken?: string
      refreshToken?: string
      sessionId?: string
      user?: any
      message?: string
      remainingSeconds?: number
    }>(`/auth/max/status/${sessionId}`)
  }

  /**
   * Проверка подтверждения телефона через MAX (Сценарий B, после регистрации).
   * POST /auth/check-max-verification
   */
  checkMaxVerification(data: { phoneNumber: string; institutionId: string | number }) {
    return useApi(true).post<{
      accessToken: string
      refreshToken: string
      sessionId: string
      user: any
    }>('/auth/check-max-verification', { body: data })
  }

  checkYandexStatus(sessionId: string) {
    return useApi(true).get<{
      status: 'pending' | 'completed' | 'expired' | 'failed'
      accessToken?: string
      refreshToken?: string
      sessionId?: string
      user?: any
      message?: string
      remainingSeconds?: number
    }>(`/auth/yandex/status/${sessionId}`)
  }

  /**
   * Обмен OAuth-токена нативного Yandex LoginSDK на сессию приложения.
   * Мобильный SDK (iOS/Android) сам проводит вход и отдаёт готовый токен — в отличие
   * от веб-потока (`initiate` → `status`), где мы инициируем авторизацию сами.
   * Бэкенд независимо проверяет токен у Яндекса и сверяет client_id.
   * POST /auth/yandex/exchange
   */
  yandexExchange(data: {
    institutionId: string
    oauthToken: string
    jwt?: string
    platform?: 'android' | 'ios'
    userAgent?: string
    clientFingerprint?: string
  }) {
    return useApi(true).post<{
      status: 'completed' | 'failed'
      accessToken: string
      refreshToken: string
      sessionId: string
      user?: {
        id: string
        firstName?: string
        lastName?: string
        email?: string
        institutionId: string
      }
      message: string
    }>('/auth/yandex/exchange', { body: data })
  }

  /**
   * Обмен токенов VK ID на сессию приложения (клиентский сценарий SDK @vkid/sdk).
   * Виджет VKID.OneTap на фронте сам меняет `code` на токены (`VKID.Auth.exchangeCode`),
   * а мы присылаем их сюда — бэкенд независимо проверяет access_token у VK и выдаёт JWT.
   * POST /auth/vk/exchange
   */
  vkExchange(data: {
    institutionId: string
    accessToken: string
    idToken?: string
    userId?: string
    deviceId?: string
    platform?: 'web' | 'android' | 'ios'
    userAgent?: string
    clientFingerprint?: string
  }) {
    return useApi(true).post<{
      status: 'completed' | 'failed'
      accessToken: string
      refreshToken: string
      sessionId: string
      user?: {
        id: string
        firstName?: string
        lastName?: string
        email?: string
        institutionId: string
      }
      message: string
    }>('/auth/vk/exchange', { body: data })
  }
}

export const apiAuth = new APIAuth()
export default apiAuth
